import { Injectable } from '@angular/core';
import { ReportDetail } from '../models/dashboard.model';

/**
 * Data structure for polygon print
 */
export interface PolygonPrintData {
  coordinates: [number, number][];
  isochrone?: any;
  url?: string; // Optional URL for QR code generation
}

/**
 * Service for managing PDF export data in sessionStorage
 */
@Injectable({
  providedIn: 'root'
})
export class PdfExportDataService {
  private readonly STORAGE_PREFIX = 'pdf-export-data-';
  private readonly POLYGON_STORAGE_PREFIX = 'polygon-print-data-';

  /**
   * Save report data to sessionStorage
   */
  saveReportData(sessionId: string, reportData: ReportDetail): void {
    const key = this.getStorageKey(sessionId);
    try {
      sessionStorage.setItem(key, JSON.stringify(reportData));
    } catch (error) {
      console.error('Failed to save report data to sessionStorage:', error);
      throw error;
    }
  }

  /**
   * Get report data from sessionStorage
   */
  getReportData(sessionId: string): ReportDetail | null {
    const key = this.getStorageKey(sessionId);
    try {
      const data = sessionStorage.getItem(key);
      if (!data) {
        return null;
      }
      return JSON.parse(data) as ReportDetail;
    } catch (error) {
      console.error('Failed to read report data from sessionStorage:', error);
      return null;
    }
  }

  /**
   * Remove report data from sessionStorage
   */
  removeReportData(sessionId: string): void {
    const key = this.getStorageKey(sessionId);
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to remove report data from sessionStorage:', error);
    }
  }

  /**
   * Check if report data exists for sessionId
   */
  hasReportData(sessionId: string): boolean {
    const key = this.getStorageKey(sessionId);
    return sessionStorage.getItem(key) !== null;
  }

  /**
   * Get storage key for sessionId
   */
  private getStorageKey(sessionId: string): string {
    return `${this.STORAGE_PREFIX}${sessionId}`;
  }

  /**
   * Save polygon data to sessionStorage
   */
  savePolygonData(sessionId: string, polygonData: PolygonPrintData): void {
    const key = this.getPolygonStorageKey(sessionId);
    try {
      sessionStorage.setItem(key, JSON.stringify(polygonData));
    } catch (error) {
      console.error('Failed to save polygon data to sessionStorage:', error);
      throw error;
    }
  }

  /**
   * Get polygon data from sessionStorage
   */
  getPolygonData(sessionId: string): PolygonPrintData | null {
    const key = this.getPolygonStorageKey(sessionId);
    try {
      const data = sessionStorage.getItem(key);
      if (!data) {
        return null;
      }
      return JSON.parse(data) as PolygonPrintData;
    } catch (error) {
      console.error('Failed to read polygon data from sessionStorage:', error);
      return null;
    }
  }

  /**
   * Remove polygon data from sessionStorage
   */
  removePolygonData(sessionId: string): void {
    const key = this.getPolygonStorageKey(sessionId);
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to remove polygon data from sessionStorage:', error);
    }
  }

  /**
   * Check if polygon data exists for sessionId
   */
  hasPolygonData(sessionId: string): boolean {
    const key = this.getPolygonStorageKey(sessionId);
    return sessionStorage.getItem(key) !== null;
  }

  /**
   * Get storage key for polygon sessionId
   */
  private getPolygonStorageKey(sessionId: string): string {
    return `${this.POLYGON_STORAGE_PREFIX}${sessionId}`;
  }
}
